<script>
import { validationMixin } from 'vuelidate'
import { required, email, minLength } from 'vuelidate/lib/validators'
import { mapActions, mapGetters } from 'vuex'
import ConfirmationResendButton from '@/components/profile/ConfirmationResendButton.vue'
import IdentityLoginButton from '@/components/identity/IdentityLoginButton.vue'
import loginFormJson from '@/data/json/homepage/loginForm.json'
import errorsJson from '@/data/json/errors/errors.json'
import validationsJson from '@/data/json/errors/validations.json'
export default {
  components: {
    ConfirmationResendButton,
    IdentityLoginButton
  },
  mixins: [validationMixin],
  validations: {
    email: { required, email },
    pwd: {
      required,
      minLength: minLength(6)
    }
  },
  data: () => ({
    content: loginFormJson.data.concat(errorsJson.data, validationsJson.data),
    devStage:
      window.config.VUE_APP_STAGE === 'DEVELOP' ||
      window.config.VUE_APP_STAGE === 'TEST',
    rememberLogin: window.config.VUE_APP_STAGE !== 'PROD',
    email: '',
    pwd: '',
    showPwd: false,
    submitError: false,
    loginErrorMsg: '',
    loading: false,
    status: null,
    dialog: true
  }),
  computed: {
    ...mapGetters(['locale']),
    emailErrors() {
      const errors = []
      if (!this.$v.email.$dirty) return errors
      !this.$v.email.email && errors.push(this.$t('validation.email.invalid'))
      !this.$v.email.required &&
        errors.push(this.$t('validation.email.required'))
      return errors
    },
    pwdErrors() {
      const errors = []
      if (!this.$v.pwd.$dirty) return errors
      !this.$v.pwd.required &&
        errors.push(this.$t('validation.password.required'))
      !this.$v.pwd.minLength &&
        errors.push(this.$t('validation.password.min.length'))
      return errors
    }
  },
  watch: {
    '$v.$error': {
      handler(hasError) {
        if (!hasError && this.submitError) this.submitError = false
      },
      deep: true
    }
  },
  created() {
    // argument is cms api-endpoint
    this.$cmsService
      .fetchContent('loginFormContents')
      .then(response => {
        if (response !== '') this.content = response
      })
      .catch(() => {})
  },
  methods: {
    ...mapActions([
      'loginUser',
      'logoutUser',
      'setPermissions',
      'getUserProfile',
      'getUserSettings',
      'getCreditBalance'
    ]),
    async submit() {
      let locale = this.locale
      this.email = this.email.trim()
      this.$v.$touch()

      if (this.$v.$invalid) {
        this.submitError = true
        this.loginErrorMsg = this.$t('validation.form.incomplete')
        return
      }
      this.loading = true
      try {
        const response = await this.loginUser({
          username: this.email,
          password: this.pwd,
          rememberLogin: this.rememberLogin,
          returnUrl: '/dashboard',
          useNewToken: true
        })
        await this.setPermissions(response.jwt)
        await this.getUserProfile()
        await this.getUserSettings()

        if (this.$permissions.hasCreditAccount) {
          await this.getCreditBalance()
        }
        let targetRoute = { name: 'dashboard', params: { locale } }

        if (this.$permissions.hasInput && this.$permissions.hasOutput) {
          targetRoute.name = 'inbox'
        } else if (this.$permissions.hasInput) {
          targetRoute.name = 'inbox'
        } else if (this.$permissions.hasOutput) {
          targetRoute.name = 'upload'
        }
        await this.$router.push(targetRoute)
      } catch (e) {
        await this.logoutUser()
        this.setFailureNotification(e)
      } finally {
        this.loading = false
      }
    },
    setFailureNotification(error) {
      let errContent = 'global.unknown.error'
      if (error?.response?.status === 401) {
        errContent = 'page.loginForm.error'
      }
      this.$notify(this.$t(errContent), { type: 'error' })
      console.error(error)
    },
    clear() {
      this.$v.$reset()
      this.status = null
      this.submitError = false
    }
  }
}
</script>
<template>
  <v-card>
    <DialogBaseColoredTitle color="white">
      <span class="grey--text text--darken-3">
        {{ $t('page.loginForm.login.label') }}</span
      >
    </DialogBaseColoredTitle>
    <v-divider></v-divider>
    <DialogBaseText class="text-center primary--text mt-4">
      <ConfirmationResendButton
        v-if="status === 410"
        :email="email"
        class="mb-8"
        @closed="clear"
      >
      </ConfirmationResendButton>
      <IdentityLoginButton v-if="devStage" />
      <v-text-field
        id="email"
        v-model="email"
        class="cms_eMail"
        :placeholder="$t('page.loginForm.email.label')"
        :error-messages="emailErrors"
        outlined
        autofocus
        dense
        required
        @input="$v.email.$touch()"
        @keyup.enter.native="submit()"
      ></v-text-field>
      <v-text-field
        id="password"
        v-model="pwd"
        class="cms_pw"
        :placeholder="$t('page.loginForm.password.label')"
        :error-messages="pwdErrors"
        :type="showPwd ? 'text' : 'password'"
        :append-icon="showPwd ? 'mdi-eye' : 'mdi-eye-off'"
        outlined
        dense
        required
        @input="$v.pwd.$touch()"
        @keyup.enter.native="submit()"
        @click:append="showPwd = !showPwd"
      ></v-text-field>
      <v-checkbox
        id="stayLoggedIn"
        v-model="rememberLogin"
        :label="$t('page.loginForm.stay.logged.in.checkbox')"
        class="mt-0"
        hide-details
      ></v-checkbox>
      <div class="text-center"></div>
    </DialogBaseText>
    <DialogBaseActions>
      <v-row justify="center">
        <v-col cols="auto">
          <v-btn
            id="forgotPassword"
            :to="{ name: 'forgotPassword', params: { locale: locale } }"
            color="primary"
            text
            outlined
            >{{ $t('button.forgot.password') }}</v-btn
          >
        </v-col>
        <v-col cols="auto">
          <v-btn
            id="routeToRegister"
            :to="{ name: 'register', params: { locale: locale } }"
            color="primary"
            text
            outlined
            >{{ $t('button.register') }}</v-btn
          >
        </v-col>
      </v-row>
    </DialogBaseActions>
    <v-divider></v-divider>
    <DialogBaseActions>
      <v-row class="ma-0">
        <v-col>
          <v-btn
            id="login"
            class="text-center px-8"
            color="primary"
            dark
            depressed
            :loading="loading"
            @click.stop="submit"
            >{{ $t('button.login') }}</v-btn
          >
        </v-col>
      </v-row>
    </DialogBaseActions>
  </v-card>
</template>
<style lang="scss" scoped>
.border--dotted {
  border-bottom: 1px dotted var(--v-primary-base);
}
</style>
